import React, { useEffect, useState, useRef } from "react";
import styles from "./SLA_Timer.module.scss";
import { TODAY_BUSINESS_HOURS } from "../../../constants";

interface SLAInfo {
  escalation_level: string;
  sla_started_at: string;
  sla_scheduled_end_at: string | null;
  sla_actual_breach_at: string | null;
  sla_duration_ms: number;
  is_resolved: boolean;
  resolved_at: string | null;
  resolution_method: string | null;
  first_response_at: string | null;
  status_at_breach: string;
  sla_emails: string[];
}

interface SLMetricsTimerProps {
  firstResponseSlaBreach: string | SLAInfo[] | null;
  statusType?: string;
  otherMailDetails?: any;
  isPaused?: boolean;
}

const SLMetricsTimer: React.FC<SLMetricsTimerProps> = ({
  firstResponseSlaBreach,
  otherMailDetails,
  isPaused = false,
}) => {
  const [timeLeft, setTimeLeft] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [isBusinessHours, setIsBusinessHours] = useState(false);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const timeLeftRef = useRef<number>(0);
  const breachStartRef = useRef<number | null>(null);

  const pausedAtRef = useRef<number | null>(null);
  const totalPausedDurationRef = useRef<number>(0);


  const formatTime = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;

    if (milliseconds <= 60000) {
      return `${String(minutes).padStart(2, "0")}m:${String(seconds).padStart(
        2,
        "0",
      )}s`;
    }

    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    const remainingMinutes = minutes % 60;
    const remainingSeconds = seconds % 60;

    if (days > 0)
      return `${days}d ${remainingHours}h ${remainingMinutes}m ${remainingSeconds}s`;
    if (hours > 0)
      return `${remainingHours}h ${remainingMinutes}m ${remainingSeconds}s`;

    return `${remainingMinutes}m ${remainingSeconds}s`;
  };


  useEffect(() => {
    const checkBusinessHours = () => {
      const data = localStorage.getItem(TODAY_BUSINESS_HOURS);
      if (!data) return setIsBusinessHours(false);

      try {
        const businessHours = JSON.parse(data);
        const now = new Date();

        const currentDay = now
          .toLocaleDateString("en-US", { weekday: "short" })
          .slice(0, 2);

        const currentTime = now.getHours() * 60 + now.getMinutes();

        const start =
          (businessHours.start_hours || 0) * 60 +
          (businessHours.start_minutes || 0);

        const end =
          (businessHours.end_hours || 24) * 60 +
          (businessHours.end_minutes || 0);

        const isWorkingDay = businessHours.day_of_week?.includes(currentDay);

        setIsBusinessHours(
          isWorkingDay &&
            currentTime >= start &&
            currentTime <= end &&
            !businessHours.is_holiday,
        );
      } catch {
        setIsBusinessHours(false);
      }
    };

    checkBusinessHours();
    const interval = setInterval(checkBusinessHours, 60000);
    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
    if (!firstResponseSlaBreach) return;

    let targetTime: Date | null = null;

    if (Array.isArray(firstResponseSlaBreach)) {
      const last = firstResponseSlaBreach[firstResponseSlaBreach.length - 1];
      if (last?.sla_scheduled_end_at)
        targetTime = new Date(last.sla_scheduled_end_at);
    } else {
      targetTime = new Date(firstResponseSlaBreach);
    }

    if (!targetTime) return;

    const diff = targetTime.getTime() - Date.now();
    timeLeftRef.current = diff > 0 ? diff : 0;
    setTimeLeft(timeLeftRef.current);
  }, [firstResponseSlaBreach]);


  useEffect(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);

    if (isPaused || !firstResponseSlaBreach) return;

    intervalRef.current = setInterval(() => {
      timeLeftRef.current = Math.max(timeLeftRef.current - 1000, 0);
      setTimeLeft(timeLeftRef.current);
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isPaused, firstResponseSlaBreach]);

  useEffect(() => {
    if (
      !Array.isArray(firstResponseSlaBreach) ||
      !firstResponseSlaBreach.length
    )
      return;

    const last = firstResponseSlaBreach[firstResponseSlaBreach.length - 1];
  

    if (last.is_resolved) return;

    const breachTime = last.sla_actual_breach_at
      ? new Date(last.sla_actual_breach_at).getTime()
      : last.sla_scheduled_end_at
      ? new Date(last.sla_scheduled_end_at).getTime()
      : null;

    if (!breachTime) return;

    breachStartRef.current = breachTime;
    totalPausedDurationRef.current = 0;
    pausedAtRef.current = null;

    if (Date.now() >= breachTime) {
      setElapsedTime(Date.now() - breachTime);
    }
  }, [firstResponseSlaBreach]);


  useEffect(() => {
    if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);

    if (!breachStartRef.current) return;

    // PAUSE
    if (isPaused) {
      pausedAtRef.current = Date.now();
      return;
    }

    // RESUME
    if (!isPaused && pausedAtRef.current) {
      totalPausedDurationRef.current += Date.now() - pausedAtRef.current;
      pausedAtRef.current = null;
    }

    elapsedIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const adjustedNow = now - totalPausedDurationRef.current;
      setElapsedTime(adjustedNow - breachStartRef.current!);
    }, 1000);

    return () => {
      if (elapsedIntervalRef.current) clearInterval(elapsedIntervalRef.current);
    };
  }, [isPaused]);

  /* ---------------- STATUS LOGIC ---------------- */

  if (!firstResponseSlaBreach) return null;

  const lastObject =
    Array.isArray(firstResponseSlaBreach) && firstResponseSlaBreach.length
      ? firstResponseSlaBreach[firstResponseSlaBreach.length - 1]
      : null;

  const isBreached =
    lastObject?.sla_actual_breach_at !== null ||
    (lastObject?.sla_scheduled_end_at &&
      new Date(lastObject.sla_scheduled_end_at).getTime() < Date.now());

  const isResolved = lastObject?.is_resolved;

  const getStatus = () => {
    if (isPaused) {
      if (isResolved) return { text: "Resolved", color: "#50cd89" };

      if (isBreached)
        return {
          text: `SL Breached + ${formatTime(elapsedTime)} (Paused)`,
          color: "red",
        };

      return {
        text: `${formatTime(timeLeft)} (Paused)`,
        color: "orange",
      };
    }

    if (isResolved) return { text: "Resolved", color: "#50cd89" };

    if (!isBusinessHours) return { text: "Off Hours", color: "orange" };

    if (isBreached)
      return {
        text: `SL Breached + ${formatTime(elapsedTime)}`,
        color: "red",
      };

    return {
      text: formatTime(timeLeft),
      color: "green",
    };
  };

  const status = getStatus();
   const last:any = firstResponseSlaBreach[firstResponseSlaBreach.length - 1];

  return (
    <div className="d-flex align-items-center">
      <span className="me-3 fs-4 fw-bolder">First Response SL:</span>
      {last.status_at_breach === "Non-actionable" ? <>
          <div
        className={`fs-4 fw-bold ${styles.timer}`}
        style={{ color: "red" }}
      >
       {last.resolution_method}
      </div>
      </> : 
      
       <div
        className={`fs-4 fw-bold ${styles.timer}`}
        style={{ color: status.color }}
      >
        {status.text}
      </div>
      
      }
     
    </div>
  );
};

export default SLMetricsTimer;
